// app/shared/components/feedback/HTFBadge.tsx
'use client';

// app/shared/components/feedback/HTFBadge.tsx
import { LucideIcon } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

export type HTFBadgeVariant = 
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'outline'
  | 'ghost';

export type HTFBadgeSize = 'sm' | 'md' | 'lg';

export interface HTFBadgeProps {
  children: React.ReactNode;
  variant?: HTFBadgeVariant;
  size?: HTFBadgeSize;
  icon?: LucideIcon;
  count?: number;
  className?: string;
  iconClassName?: string;
  rounded?: 'default' | 'full';
  onClick?: () => void;
}

export function HTFBadge({
  children,
  variant = 'default',
  size = 'md',
  icon: Icon,
  count,
  className,
  iconClassName,
  rounded = 'default',
  onClick,
}: HTFBadgeProps) {
  // Variant styles
  const variantStyles: Record<HTFBadgeVariant, string> = {
    default: 'bg-gray-100 text-gray-800 border-gray-200',
    primary: 'bg-primary/20 text-primary border-primary/30',
    secondary: 'bg-secondary text-white border-0',
    success: 'bg-success/20 text-success border-success/30',
    warning: 'bg-warning/20 text-warning-dark border-warning/30',
    error: 'bg-error/20 text-error border-error/30',
    info: 'bg-info/20 text-info-dark border-info/30',
    outline: 'bg-transparent border-gray-300 text-gray-700',
    ghost: 'bg-transparent text-gray-700',
  };
  
  // Size styles
  const sizeStyles: Record<HTFBadgeSize, string> = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-xs px-2 py-1',
    lg: 'text-sm px-2.5 py-1',
  };
  
  // Icon size mapping
  const iconSizes: Record<HTFBadgeSize, number> = {
    sm: 12,
    md: 14,
    lg: 16,
  };
  
  // Border radius
  const getRoundedStyle = () => {
    if (rounded === 'full') {
      return variant === 'secondary' ? 'rounded-lg' : 'rounded-full';
    }
    return 'rounded';
  };
  
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 font-medium",
        variant !== 'secondary' && "border",
        variantStyles[variant],
        sizeStyles[size],
        getRoundedStyle(),
        onClick && "cursor-pointer hover:opacity-80",
        className
      )}
      onClick={onClick}
    >
      {Icon && (
        <Icon 
          size={iconSizes[size]} 
          className={cn("flex-shrink-0", iconClassName)}
        />
      )}
      {children}
      {count !== undefined && (
        <span 
          className={cn(
            "ml-1 px-1 rounded-full text-xs font-bold",
            variant === 'outline' || variant === 'ghost' 
              ? 'bg-gray-200 text-gray-700' 
              : 'bg-white/80 text-gray-800'
          )}
        >
          {count}
        </span>
      )}
    </span>
  );
} 